import Badge from '@/common/components/Badge';
import Button from '@/common/components/Button';
import Tabs from '@/common/components/Tabs';
import useCompany from '@/common/hooks/queries/useCompany';
import useCompanyEquipment from '@/common/hooks/queries/useCompanyEquipment';
import { useCurrentTabInfo } from '@/common/hooks/useCurrentTabInfo';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { companyEquipmentsTabs } from './constants/tabs.constant';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { ShowHeader } from '@/common/components/ShowHeader';
import { CarFrontIcon } from 'lucide-react';

const Show = () => {
  const { companyId, id: equipmentId } = useParams()

  const { data: company } = useCompany({ id: companyId || '' })

  const {
    data: equipment,
    error: equipmentError = {} as any,
    refetch: refetchEquipment,
    isPending: equipmentIsPending,
    dataUpdatedAt: equipmentUpdatedAt,
  } = useCompanyEquipment({ id: equipmentId || '' });

  const tabsList = companyEquipmentsTabs(equipment, equipmentId, company);

  const { currentTab } = useCurrentTabInfo(tabsList);

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const equipmentDisplayName = useMemo(() => {
    return (categoryFieldForDisplayName && equipment[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
      equipment?.license_plate ||
      equipment?.official_name ||
      equipment?.serial_number ||
      'Não informado';
  }, [equipmentUpdatedAt]);

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={equipmentError?.status}
      refetchFunction={refetchEquipment}
      notFoundFallback={`/companies/${companyId}/equipments`}
      notFoundButtonText='Ir para Equipamentos'
    >
      <div className='flex flex-col max-h-[calc(100vh-64px)]'>
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/companies/${companyId}/equipments`,
            searchLastRouteFromReference: `/companies/${companyId}/equipments/${equipmentId}`,
          }}
          isLoading={equipmentIsPending}
          titleClassName="text-white-400 font-normal text-base"
          title={company?.general_information?.fantasy_name}
          subtitle={(
            <div className='flex items-center gap-1.5'>
              {categoryFieldForDisplayName === 'license_plate' && (
                <CarFrontIcon strokeWidth={1.5} size={18} />
              )}

              <h2 data-testid="equipment_show_display_name">
                {equipmentDisplayName}
              </h2>
            </div>
          )}
          badge={(
            <>
              {equipment?.status === 'blocked' && (
                <Badge
                  Icon={React.Fragment}
                  text='Bloqueado'
                />
              )}

              {equipment?.status === 'disabled' && (
                <Badge
                  Icon={React.Fragment}
                  text='Desativado'
                />
              )}</>
          )}
          buttons={(
            <>
              {!currentTab && (
                <Link to={`/companies/${companyId}/equipments/${equipmentId}/edit`}>
                  <Button
                    name="edit_company_equipment"
                    type="button"
                    className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                    operationKlassToCheckPermission='Admin::Client::Equipments::Update'
                  >
                    Editar
                  </Button>
                </Link>
              )}
            </>
          )}
        />

        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6 flex flex-col max-h-[calc(100vh-186px)]"
          tabsList={tabsList}
        />
      </div>
    </ErrorMessageWrapper>
  )
}

export default Show
